import React, { useState, useMemo } from 'react';
import Input from '../../components/Form/Input';
import Select from '../../components/Form/Select';
import Button from '../../components/Form/Button';
import { FinanceReport } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useInvoices } from '../../hooks/useInvoices';
import { usePurchases } from '../../hooks/usePurchases';
import { usePaymentOrders } from '../../hooks/usePaymentOrders';
import { useBills } from '../../hooks/useBills';
import { useClients } from '../../hooks/useClients';
import { useProviders } from '../../hooks/useProviders';
import { useBranches } from '../../hooks/useBranches';
import { useInvoiceHires } from '../../hooks/useInvoiceHires';
import { useCurrencies } from '../../hooks/useCurrencies';
import { PageHeader } from '../../components/Common';
import ReportGenerationForm from './ReportGenerationForm';
import ReportSummary from './ReportSummary';
import ReportTransactionsTable from './ReportTransactionsTable';
import { generateReportData, calculateSummary } from './reportDataGenerator';

const REPORT_TYPE_OPTIONS = [
  { value: 'all_modules', label: 'All Systems Report' },
  { value: 'usd_only', label: 'USD Only Report' },
  { value: 'conversion_difference', label: 'Currency Conversion Report' }
];

const MODULE_OPTIONS = [
  { value: 'all', label: 'All Systems' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'invoice-hire', label: 'Invoice Hire/ACM' },
  { value: 'purchases', label: 'Purchases' },
  { value: 'bills', label: 'Bills' },
  { value: 'payment-orders', label: 'Payment Orders' }
];

const REPORT_TITLES: Record<string, string> = {
  all_modules: 'Comprehensive Financial Report - All Systems',
  usd_only: 'USD Currency Report',
  conversion_difference: 'Currency Conversion Difference Report',
};

const REPORT_DESCRIPTIONS: Record<string, string> = {
  all_modules: 'A comprehensive financial overview across all systems including income and expenses',
  usd_only: 'Report showing only USD transactions and their impact',
  conversion_difference: 'Analysis of currency conversion differences between USD and EGP',
};

const FinanceReports: React.FC = () => {
  const [reportType, setReportType] = useState<'all_modules' | 'usd_only' | 'conversion_difference'>('all_modules');
  const [dateFrom, setDateFrom] = useState<string>('2024-01-01');
  const [dateTo, setDateTo] = useState<string>(new Date().toISOString().split('T')[0]);
  const [exchangeRate, setExchangeRate] = useState<number>(50.0);
  const [selectedModule, setSelectedModule] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [generatedReport, setGeneratedReport] = useState<FinanceReport | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const { data: invoices, isLoading: invoicesLoading } = useInvoices({ populate: '*' });
  const { data: purchases, isLoading: purchasesLoading } = usePurchases({ populate: '*' });
  const { data: paymentOrders, isLoading: paymentOrdersLoading } = usePaymentOrders({ populate: '*' });
  const { data: bills, isLoading: billsLoading } = useBills({ populate: '*' });
  const { data: clients, isLoading: clientsLoading } = useClients({ populate: '*' });
  const { data: providers, isLoading: providersLoading } = useProviders({ populate: '*' });
  const { data: branches, isLoading: branchesLoading } = useBranches({ populate: '*' });
  const { data: invoiceHires, isLoading: invoiceHiresLoading } = useInvoiceHires({ populate: '*' });
  const { data: currencies, isLoading: currenciesLoading } = useCurrencies({ populate: '*' });

  const isLoading = invoicesLoading || purchasesLoading || paymentOrdersLoading || billsLoading || 
                    clientsLoading || providersLoading || branchesLoading || invoiceHiresLoading || currenciesLoading;

  const currentExchangeRate = useMemo(() => {
    if (currencies && Array.isArray(currencies)) {
      const usd = currencies.find((c: any) => c.code === 'USD');
      if (usd) return usd.exchangeRateToEGP || exchangeRate;
    }
    return exchangeRate;
  }, [currencies, exchangeRate]);

  React.useEffect(() => {
    if (!isLoading) handleGenerateReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, dateFrom, dateTo, selectedModule, exchangeRate, isLoading]);

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    await new Promise(resolve => setTimeout(resolve, generatedReport ? 200 : 500));
    const deps = { invoices, invoiceHires, purchases, bills, paymentOrders, clients, providers, branches, currencies, currentExchangeRate };
    const data = generateReportData(deps, { reportType, selectedModule, dateFrom, dateTo });
    const summary = calculateSummary(data);
    setGeneratedReport({
      id: Date.now().toString(), reportType,
      title: REPORT_TITLES[reportType] || 'Financial Report',
      description: REPORT_DESCRIPTIONS[reportType] || 'Financial Report',
      dateRange: { from: dateFrom || '2024-01-01', to: dateTo || new Date().toISOString().split('T')[0] },
      currency: reportType === 'usd_only' ? 'USD' : 'ALL', exchangeRate: currentExchangeRate,
      data, summary, generatedAt: new Date().toISOString(), generatedBy: 'System User'
    });
    setIsGenerating(false);
  };

  const filteredReportData = useMemo(() => {
    if (!generatedReport) return [];
    return generatedReport.data.filter(item => {
      const matchesSearch = !searchTerm || (
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.module.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.clientVessel && item.clientVessel.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      return matchesSearch && (statusFilter === 'all' || item.status === statusFilter);
    });
  }, [generatedReport, searchTerm, statusFilter]);

  const mapExportRow = (item: any) => ({
    'Module': item.module, 'Category': item.category, 'Description': item.description,
    'Date': item.date, 'Amount (USD)': item.convertedAmount?.usd || 0,
    'Amount (EGP)': item.convertedAmount?.egp || 0, 'Exchange Rate': item.conversionRate,
    'Status': item.status, 'Reference': item.reference, 'Client/Vessel': item.clientVessel || ''
  });

  const handleExport = () => {
    if (!generatedReport) return;
    exportToExcel(filteredReportData.map(mapExportRow), `finance_report_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    if (!generatedReport) return;
    printAsExcel(filteredReportData.map(mapExportRow), 'Finance Report');
  };

  const handleExportCSV = () => {
    if (!generatedReport) return;
    const csvContent = "data:text/csv;charset=utf-8,"
      + "Module,Category,Description,Date,USD Amount,EGP Amount,Conversion Rate,Conversion Difference,Status,Reference,Client/Vessel\n"
      + generatedReport.data.map(item =>
          `"${item.module}","${item.category}","${item.description}","${item.date}",${item.convertedAmount?.usd || 0},${item.convertedAmount?.egp || 0},${item.conversionRate || 0},${item.conversionDifference || 0},"${item.status}","${item.reference}","${item.clientVessel || ''}"`
        ).join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", `finance_report_${reportType}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Finance Reports" description="Generate comprehensive financial reports with currency tracking and conversion analysis" />

      {isLoading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800">Loading financial data from APIs...</p>
        </div>
      )}

      <ReportGenerationForm
        reportType={reportType} onReportTypeChange={(v) => setReportType(v as any)}
        selectedModule={selectedModule} onModuleChange={setSelectedModule}
        exchangeRate={exchangeRate} onExchangeRateChange={setExchangeRate}
        dateFrom={dateFrom} onDateFromChange={setDateFrom}
        dateTo={dateTo} onDateToChange={setDateTo}
        onGenerate={handleGenerateReport} isGenerating={isGenerating} isLoading={isLoading}
        reportTypeOptions={REPORT_TYPE_OPTIONS} moduleOptions={MODULE_OPTIONS}
      />

      {generatedReport && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Filter Transactions</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Input label="Search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search transactions..." />
              </div>
              <div>
                <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} options={[
                  { value: 'all', label: 'All Statuses' }, { value: 'pending', label: 'Pending' },
                  { value: 'paid', label: 'Paid' }, { value: 'approved', label: 'Approved' },
                  { value: 'delivered', label: 'Delivered' }, { value: 'overdue', label: 'Overdue' },
                  { value: 'cancelled', label: 'Cancelled' }
                ]} />
              </div>
              <div className="flex items-end">
                <Button onClick={() => { setSearchTerm(''); setStatusFilter('all'); }} variant="secondary" size="sm" className="w-full">
                  Clear Filters
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {generatedReport && (
        <>
          <ReportSummary report={generatedReport} onExportCSV={handleExportCSV} />
          <ReportTransactionsTable
            data={filteredReportData} totalCount={generatedReport.data.length}
            reportType={reportType} onExport={handleExport} onPrint={handlePrint}
          />
        </>
      )}
    </div>
  );
};

export default FinanceReports;
